import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  BORDER, ChartCard, ChartSkeleton, NoData, ShareBar, Stat, StatStrip, SubText, TABLE_SX
} from '../../OnlineJournal/components/analytics/chartKit';
import { deltaPct, fmtDelta, fmtInt, fmtPct } from '../../OnlineJournal/format';
import { useLoad } from '../../OnlineJournal/hooks/useLoad';
import { GROUP_COLORS, WATCH_COLORS } from '../constants';
import { plural } from '../format';
import { DocumentFilters } from '../types';
import { analyticsApi } from './analyticsApi';
import { fmtHours, fmtPack, KIND_COLORS, SLICE_LABELS, SLICE_PARAMS, sliceText } from './analyticsFormat';
import { MiniToggle } from './analyticsKit';
import { Period, ProductRow, ProductSlice, ProductsKpi, ProductsResponse, SliceKey, ViewProps } from './analyticsTypes';
import ProductCard from './ProductCard';

type Slices = Partial<Record<SliceKey, string>>;
type SideSlice = Exclude<SliceKey, 'producer'>;

const SIDE_SLICES: SideSlice[] = ['type', 'rxotc', 'folder', 'mnn'];
const PAGE = 50;
const SEARCH_DELAY = 400;

/** Изменение к прошлому периоду для подписи плитки */
function trend(cur: number, prev: number, fmt: (v: number) => string): string {
  const d = deltaPct(cur, prev);
  return d === null ? 'в прошлом периоде не было' : `${fmtDelta(d)} · было ${fmt(prev)}`;
}

function kpiStats(cur: ProductsKpi, prev: ProductsKpi): Stat[] {
  return [
    { label: 'Товаров двигалось', value: fmtInt(cur.products), hint: trend(cur.products, prev.products, fmtInt) },
    { label: 'Упаковок', value: fmtPack(cur.qty), hint: trend(cur.qty, prev.qty, fmtPack) },
    { label: 'Строк накладных', value: fmtInt(cur.lines), hint: `в среднем ${fmtPack(cur.lines ? cur.qty / cur.lines : null)} уп. на строку` },
    {
      label: 'Нехватка остатка', value: `${fmtInt(cur.shortProducts)} ${plural(cur.shortProducts, 'товар', 'товара', 'товаров')}`,
      color: cur.shortProducts ? WATCH_COLORS.warn : undefined, hint: `${fmtInt(cur.shortLines)} строк · не хватило ${fmtPack(cur.shortQty)} уп.`,
      tooltip: 'Строки, по которым 1С ответила «Не достаточно остатка»'
    },
    { label: 'Маркированные строки', value: fmtPct(cur.markedShare), hint: `было ${fmtPct(prev.markedShare)}`, tooltip: 'Доля строк, по которым отсканированы коды маркировки' },
    { label: 'Возвраты', value: fmtPct(cur.returnShare), hint: `было ${fmtPct(prev.returnShare)}`, tooltip: 'Доля упаковок, ушедших возвратом' },
    { label: 'Строки по 1 уп.', value: fmtPct(cur.singleShare), hint: `было ${fmtPct(prev.singleShare)}`, tooltip: 'Доля строк с количеством 1: много мелких перемещений' }
  ];
}

/** Мини-график упаковок по дням / неделям периода */
const Spark: React.FC<{ values: number[] }> = ({ values }) => {
  const W = 84;
  const H = 22;
  const max = Math.max(0, ...values);
  if (!max || values.length < 2) return <Box sx={{ width: W, height: H }} />;
  const step = W / (values.length - 1);
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(H - 1 - (v / max) * (H - 2)).toFixed(1)}`).join(' ');
  return (
    <svg width={W} height={H} style={{ display: 'block' }} aria-hidden>
      <polygon points={`0,${H} ${points} ${W},${H}`} fill="rgba(37,99,235,0.12)" />
      <polyline points={points} fill="none" stroke={KIND_COLORS.direct} strokeWidth={1.25} />
    </svg>
  );
};

/** Варианты среза: клик - отфильтровать товары, повторный клик - снять */
const SliceCard: React.FC<{
  title: string; subtitle: string; slice: ProductSlice; selected?: string; onPick: (value: string) => void; actions?: React.ReactNode;
}> = ({ title, subtitle, slice, selected, onPick, actions }) => {
  const max = slice.items.reduce((m, i) => Math.max(m, i.qty), 0);
  return (
    <ChartCard title={title} subtitle={subtitle} actions={actions}>
      {!slice.items.length ? <NoData height={160} text="Нет товаров" /> : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {slice.items.map(i => {
            const on = selected !== undefined && selected.toLowerCase() === i.value.toLowerCase();
            return (
              <Box
                key={i.value} component="button" type="button" onClick={() => onPick(i.value)}
                sx={{
                  display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 150px 52px', alignItems: 'center', gap: 1, px: 1, py: 0.5,
                  border: `1px solid ${on ? '#bfdbfe' : 'transparent'}`, borderRadius: 1.5, bgcolor: on ? '#eff6ff' : 'transparent',
                  font: 'inherit', textAlign: 'left', cursor: 'pointer', '&:hover': { bgcolor: on ? '#eff6ff' : '#f8fafc' }
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: i.value === '__none' ? '#94a3b8' : '#0f172a' }} noWrap title={sliceText(i.value)}>
                    {sliceText(i.value)}
                  </Typography>
                  <SubText>{fmtInt(i.products)} {plural(i.products, 'товар', 'товара', 'товаров')}</SubText>
                </Box>
                <ShareBar value={i.qty} max={max} label={fmtPack(i.qty)} />
                <Typography sx={{ fontSize: '0.72rem', color: '#64748b', textAlign: 'right' }}>{fmtPct(i.share)}</Typography>
              </Box>
            );
          })}
          {slice.other && (
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', px: 1, pt: 0.5 }}>
              Ещё {fmtInt(slice.other.values)} {plural(slice.other.values, 'значение', 'значения', 'значений')}: {fmtInt(slice.other.products)} товаров, {fmtPack(slice.other.qty)} уп.
            </Typography>
          )}
        </Box>
      )}
    </ChartCard>
  );
};

type SortKey = 'name' | 'qty' | 'delta' | 'docs' | 'lines' | 'senders' | 'returnShare' | 'viaShare' | 'errorShare' | 'shortLines' | 'markedShare' | 'singleShare' | 'p50Hours';

const sortValue = (p: ProductRow, key: SortKey): number | string | null => {
  if (key === 'delta') return deltaPct(p.qty, p.prevQty);
  return p[key];
};

/** Сравнение с пустыми значениями в конце при любом направлении */
function compare(a: number | string | null, b: number | string | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir;
  return String(a).localeCompare(String(b), 'ru', { numeric: true }) * dir;
}

const COLUMNS: Array<{ key: SortKey | null; label: string; align?: 'right'; tooltip?: string }> = [
  { key: 'name', label: 'Товар' },
  { key: 'qty', label: 'Упаковок', align: 'right' },
  { key: 'delta', label: 'К прошлому', align: 'right' },
  { key: null, label: 'Динамика' },
  { key: 'docs', label: 'Документов', align: 'right' },
  { key: 'lines', label: 'Строк', align: 'right' },
  { key: 'senders', label: 'Отпр. / получ.', align: 'right', tooltip: 'Сколько аптек и складов отправляли и получали товар' },
  { key: 'returnShare', label: 'Возвраты', align: 'right', tooltip: 'Доля упаковок, ушедших возвратом' },
  { key: 'viaShare', label: 'Через РЦ', align: 'right', tooltip: 'Доля упаковок, перемещённых через РЦ' },
  { key: 'errorShare', label: 'Ошибки', align: 'right', tooltip: 'Доля документов с товаром, которые сейчас в статусах ошибки' },
  { key: 'shortLines', label: 'Нехватка', align: 'right', tooltip: 'Строки с ответом 1С «Не достаточно остатка»' },
  { key: 'markedShare', label: 'Маркировка', align: 'right', tooltip: 'Доля строк с отсканированными кодами маркировки' },
  { key: 'singleShare', label: 'По 1 уп.', align: 'right', tooltip: 'Доля строк с количеством 1' },
  { key: 'p50Hours', label: 'Медиана', align: 'right', tooltip: 'Медиана от создания до «Завершен» по документам с товаром' }
];

const productSub = (p: { sku: string | null; producer: string | null }) => [p.sku ? `арт. ${p.sku}` : null, p.producer].filter(Boolean).join(' · ') || '-';

const ProductsTable: React.FC<{ data: ProductsResponse; onOpen: (id: string) => void }> = ({ data, onOpen }) => {
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'qty', dir: -1 });
  const [shown, setShown] = useState(PAGE);
  const maxQty = data.products.reduce((m, p) => Math.max(m, p.qty), 0);
  const rows = useMemo(
    () => data.products.map((p, i) => ({ p, rank: i + 1 })).sort((a, b) => compare(sortValue(a.p, sort.key), sortValue(b.p, sort.key), sort.dir)),
    [data.products, sort]
  );
  const toggleSort = (key: SortKey) => {
    setSort(s => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'name' ? 1 : -1 }));
    setShown(PAGE);
  };
  const pct = (v: number | null, color?: string) => (
    <Box component="span" sx={{ color: v ? color : '#cbd5e1' }}>{v ? fmtPct(v) : '-'}</Box>
  );

  return (
    <ChartCard
      wide title="Товары"
      subtitle={`Двигалось ${fmtInt(data.total)} ${plural(data.total, 'товар', 'товара', 'товаров')}${data.total > data.products.length ? `, в таблице - ${fmtInt(data.products.length)} крупнейших по упаковкам` : ''}. Клик по строке - карточка товара.`}
    >
      {!rows.length ? <NoData height={120} text="Ничего не найдено" /> : (
        <>
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: 1.5, maxHeight: 640 }}>
            <Table size="small" stickyHeader sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 32 }}>№</TableCell>
                  {COLUMNS.map(c => (
                    <TableCell key={c.label} align={c.align} title={c.tooltip}>
                      {c.key ? (
                        <TableSortLabel active={sort.key === c.key} direction={sort.key === c.key && sort.dir === 1 ? 'asc' : 'desc'} onClick={() => toggleSort(c.key as SortKey)}>
                          {c.label}
                        </TableSortLabel>
                      ) : c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, shown).map(({ p, rank }) => {
                  const d = deltaPct(p.qty, p.prevQty);
                  return (
                    <TableRow key={p.id} hover onClick={() => onOpen(p.id)} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ color: '#94a3b8 !important' }}>{rank}</TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }} noWrap title={p.name}>{p.name}</Typography>
                        <SubText color={p.inCatalog ? undefined : WATCH_COLORS.warn}>{p.inCatalog ? productSub(p) : 'нет в справочнике 1С'}</SubText>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 130 }}><ShareBar value={p.qty} max={maxQty} label={fmtPack(p.qty)} /></TableCell>
                      <TableCell align="right" sx={{ color: d === null ? undefined : `${d >= 0 ? '#15803d' : '#b91c1c'} !important` }}>
                        {d === null ? 'новый' : fmtDelta(d)}
                        <SubText>{p.prevQty ? `было ${fmtPack(p.prevQty)}` : 'не двигался'}</SubText>
                      </TableCell>
                      <TableCell><Spark values={p.spark} /></TableCell>
                      <TableCell align="right">{fmtInt(p.docs)}</TableCell>
                      <TableCell align="right">{fmtInt(p.lines)}</TableCell>
                      <TableCell align="right">{fmtInt(p.senders)} / {fmtInt(p.recipients)}</TableCell>
                      <TableCell align="right">{pct(p.returnShare)}</TableCell>
                      <TableCell align="right">{pct(p.viaShare)}</TableCell>
                      <TableCell align="right">{pct(p.errorShare, GROUP_COLORS.error)}</TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={{ color: p.shortLines ? WATCH_COLORS.warn : '#cbd5e1', fontWeight: p.shortLines ? 600 : 400 }}>{p.shortLines ? fmtInt(p.shortLines) : '-'}</Box>
                      </TableCell>
                      <TableCell align="right">{pct(p.markedShare)}</TableCell>
                      <TableCell align="right">{pct(p.singleShare)}</TableCell>
                      <TableCell align="right">{fmtHours(p.p50Hours)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length > shown && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" onClick={() => setShown(s => s + PAGE)} sx={{ textTransform: 'none' }}>
                Показать ещё {Math.min(PAGE, rows.length - shown)} из {fmtInt(rows.length - shown)}
              </Button>
            </Box>
          )}
        </>
      )}
    </ChartCard>
  );
};

interface ProblemItem { id: string; name: string; sub: string; value: string }

/** Список проблемных товаров: клик - карточка товара */
const ProblemCard: React.FC<{ title: string; subtitle: string; color: string; items: ProblemItem[]; empty: string; onOpen: (id: string) => void }> = ({
  title, subtitle, color, items, empty, onOpen
}) => (
  <ChartCard title={title} subtitle={subtitle}>
    {!items.length ? <NoData height={80} text={empty} /> : (
      <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 360, overflowY: 'auto' }}>
        {items.map(i => (
          <Box
            key={i.id} component="button" type="button" onClick={() => onOpen(i.id)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, px: 0.75, py: 0.6, border: 'none', borderBottom: '1px solid #f1f5f9',
              bgcolor: 'transparent', font: 'inherit', textAlign: 'left', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }
            }}
          >
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f172a' }} noWrap title={i.name}>{i.name}</Typography>
              <SubText>{i.sub}</SubText>
            </Box>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color, whiteSpace: 'nowrap' }}>{i.value}</Typography>
          </Box>
        ))}
      </Box>
    )}
  </ChartCard>
);

const Problems: React.FC<{ data: ProductsResponse; onOpen: (id: string) => void }> = ({ data, onOpen }) => {
  const p = data.problems;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
      <ProblemCard
        title="Нехватка остатка" subtitle="1С ответила «Не достаточно остатка»: сколько упаковок не хватило" color={WATCH_COLORS.warn} onOpen={onOpen}
        empty="Нехватки не было"
        items={p.shortage.map(x => ({ id: x.id, name: x.name, sub: `${fmtInt(x.shortLines)} из ${fmtInt(x.lines)} строк · ${productSub(x)}`, value: `${fmtPack(x.shortQty)} уп.` }))}
      />
      <ProblemCard
        title="Ошибки и автоочистка" subtitle="Документы с товаром в статусах ошибки и убранные ночной автоочисткой" color={GROUP_COLORS.error} onOpen={onOpen}
        empty="Ошибок нет"
        items={p.failed.map(x => ({
          id: x.id, name: x.name, value: `${fmtInt(x.errorDocs + x.cleanedDocs)} из ${fmtInt(x.docs)}`,
          sub: `ошибки ${fmtInt(x.errorDocs)}${x.rejectedDocs ? ` (отклонено ${fmtInt(x.rejectedDocs)})` : ''} · автоочистка ${fmtInt(x.cleanedDocs)}`
        }))}
      />
      <ProblemCard
        title="Запрет перемещения в 1С" subtitle="В справочнике стоит запрет перемещения, но товар перемещали (возвраты не учитываются)" color={GROUP_COLORS.error}
        onOpen={onOpen} empty="Нарушений нет"
        items={p.violations.map(x => ({ id: x.id, name: x.name, sub: `${fmtInt(x.docs)} док. · ${productSub(x)}`, value: `${fmtPack(x.qty)} уп.` }))}
      />
      {!data.catalogError && (
        <ProblemCard
          title={`Нет в справочнике 1С${p.unknownTotal ? ` (${fmtInt(p.unknownTotal)})` : ''}`} subtitle="Товар есть в накладных, но не найден в номенклатуре" color="#64748b"
          onOpen={onOpen} empty="Все товары есть в справочнике"
          items={p.unknown.map(x => ({ id: x.id, name: x.name, sub: `${fmtInt(x.docs)} док.`, value: `${fmtPack(x.qty)} уп.` }))}
        />
      )}
      <ProblemCard
        title="Пропали из движения" subtitle="Двигались в прошлом периоде, в текущем - ни одной строки" color="#64748b" onOpen={onOpen}
        empty="Таких товаров нет"
        items={p.gone.map(x => ({ id: x.id, name: x.name, sub: `${fmtInt(x.prevDocs)} док. в прошлом периоде · ${productSub(x)}`, value: `было ${fmtPack(x.prevQty)} уп.` }))}
      />
    </Box>
  );
};

/** Товары: показатели, срезы по справочнику 1С (первым - производитель), таблица, проблемные товары и карточка товара */
const ProductsView: React.FC<ViewProps> = ({ params, paramsKey, period, active, reloadToken, onCohort }) => {
  const [slices, setSlices] = useState<Slices>({});
  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');
  const [side, setSide] = useState<SideSlice>('type');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setTerm(input.trim()), SEARCH_DELAY);
    return () => clearTimeout(t);
  }, [input]);

  const query = useMemo(() => {
    const q: Record<string, string> = { ...params };
    (Object.keys(slices) as SliceKey[]).forEach(k => { const v = slices[k]; if (v) q[SLICE_PARAMS[k]] = v; });
    if (term) q.q = term;
    return q;
  }, [params, slices, term]);

  const state = useLoad(
    active ? (_silent: boolean, signal: AbortSignal) => analyticsApi.products(query, signal) : null,
    `products|${paramsKey}|${JSON.stringify(slices)}|${term}`, reloadToken, 'Не удалось построить аналитику по товарам'
  );
  const data = state.data;
  const stats = useMemo(() => (data ? kpiStats(data.kpi.cur, data.kpi.prev) : []), [data]);

  const pick = (key: SliceKey) => (value: string) => setSlices(s => {
    const next = { ...s };
    if (next[key] && next[key]!.toLowerCase() === value.toLowerCase()) delete next[key];
    else next[key] = value;
    return next;
  });
  const drop = (key: SliceKey) => setSlices(s => { const next = { ...s }; delete next[key]; return next; });
  const cohort = (extra: Partial<DocumentFilters>, range?: Period) => { setOpen(null); onCohort(extra, range); };
  const filtered = Boolean(term) || Object.keys(slices).length > 0;

  if (!data) {
    return (
      <ChartCard wide title="Товары" loading={state.loading} error={state.error ? state.error.text : null} onRetry={state.retry}>
        {!state.error && <ChartSkeleton height={420} />}
      </ChartCard>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: state.loading ? 0.65 : 1, transition: 'opacity 0.15s' }}>
      {state.error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={state.retry}>Повторить</Button>}>
          {state.error.text}. На экране - данные прежнего запроса.
        </Alert>
      )}
      {data.catalogError && (
        <Alert severity="warning">Справочник 1С недоступен: срезы и признаки товаров не показаны, названия взяты из накладных.</Alert>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Название, артикул, МНН, производитель" value={input} onChange={e => setInput(e.target.value)}
          sx={{ width: 340, bgcolor: '#fff', '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
        />
        {(Object.keys(slices) as SliceKey[]).map(k => (
          <Chip key={k} size="small" label={`${SLICE_LABELS[k]}: ${sliceText(slices[k] || '')}`} onDelete={() => drop(k)} sx={{ maxWidth: 320 }} />
        ))}
        {filtered && (
          <Button size="small" onClick={() => { setSlices({}); setInput(''); setTerm(''); }} sx={{ textTransform: 'none' }}>Сбросить</Button>
        )}
      </Box>
      <StatStrip items={stats} />
      {!data.catalogError && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 1.5 }}>
          <SliceCard
            title="Производители" slice={data.slices.producer} selected={slices.producer} onPick={pick('producer')}
            subtitle={`${fmtInt(data.slices.producer.values)} ${plural(data.slices.producer.values, 'производитель', 'производителя', 'производителей')} в периоде. Упаковки и доля от всех упаковок; клик - отфильтровать товары.`}
          />
          <SliceCard
            title={SLICE_LABELS[side]} slice={data.slices[side]} selected={slices[side]} onPick={pick(side)}
            subtitle={`${fmtInt(data.slices[side].values)} ${plural(data.slices[side].values, 'значение', 'значения', 'значений')}. Клик - отфильтровать товары.`}
            actions={<MiniToggle<SideSlice> value={side} onChange={setSide} options={SIDE_SLICES.map(k => ({ value: k, label: SLICE_LABELS[k] }))} />}
          />
        </Box>
      )}
      <ProductsTable data={data} onOpen={setOpen} />
      <Problems data={data} onOpen={setOpen} />
      {open && (
        <ProductCard
          key={open} id={open} params={params} paramsKey={paramsKey} period={period} reloadToken={reloadToken}
          onOpen={setOpen} onClose={() => setOpen(null)} onCohort={cohort}
        />
      )}
    </Box>
  );
};

export default React.memo(ProductsView);
